import { useState, useEffect, useCallback } from 'react';
import { useConnectionQuality } from './useConnectionQuality';

export interface UseOptimizedImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png' | 'auto';
  placeholder?: string;
  lazyLoad?: boolean;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

/**
 * A hook for optimizing image loading based on connection quality
 */
export function useOptimizedImage(
  src: string | null | undefined,
  options: UseOptimizedImageOptions = {}
) {
  const {
    width,
    height,
    quality,
    format = 'auto',
    placeholder = '/placeholder.svg',
    lazyLoad = true,
    onLoad,
    onError
  } = options;
  
  const { isSlowConnection, getImageQuality } = useConnectionQuality();
  const [optimizedSrc, setOptimizedSrc] = useState<string>(placeholder);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Get quality setting based on connection
  const getOptimalQuality = useCallback(() => {
    if (quality) return quality;
    
    const connectionQuality = getImageQuality();
    switch (connectionQuality) {
      case 'low':
        return 60;
      case 'medium':
        return 75;
      case 'high':
        return 90;
      default:
        return 80;
    }
  }, [getImageQuality, quality]);
  
  // Generate optimized image URL
  const generateOptimizedUrl = useCallback((originalSrc: string) => {
    if (!originalSrc) return placeholder;
    
    // If the image is from Cloudflare R2 (media.hivetivity.com), do NOT add query params
    if (originalSrc.includes('media.hivetivity.com')) {
      return originalSrc;
    }
    
    // Check if this is a URL that can be optimized (e.g., has query params available)
    try {
      const url = new URL(originalSrc);
      
      // If we're on a slow connection, reduce dimensions
      const scaleFactor = isSlowConnection ? 0.75 : 1;
      const optimalQuality = getOptimalQuality();
      
      // Only add parameters for images we can optimize
      const canOptimize = /\.(jpe?g|png|webp|gif|svg)$/i.test(url.pathname);
      if (canOptimize) {
        // Add width if specified
        if (width) {
          url.searchParams.set('w', Math.round(width * scaleFactor).toString());
        }
        
        // Add height if specified
        if (height) {
          url.searchParams.set('h', Math.round(height * scaleFactor).toString());
        }
        
        // Add quality parameter
        url.searchParams.set('q', optimalQuality.toString());
        
        // Add format if not 'auto'
        if (format !== 'auto') {
          url.searchParams.set('fmt', format);
        }
        
        return url.toString();
      }
      
      return originalSrc;
    } catch (e) {
      // Not a valid URL or cannot be parsed, return original
      return originalSrc;
    }
  }, [width, height, format, isSlowConnection, getOptimalQuality, placeholder]);
  
  // Load the image
  useEffect(() => {
    if (!src) {
      setOptimizedSrc(placeholder);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    const optimizedUrl = generateOptimizedUrl(src);
    
    if (lazyLoad) {
      // Use IntersectionObserver for lazy loading
      const img = new Image();
      
      img.onload = () => {
        setOptimizedSrc(optimizedUrl);
        setIsLoading(false);
        if (onLoad) onLoad();
      };
      
      img.onerror = (e) => {
        const err = new Error(`Failed to load image: ${optimizedUrl}`);
        setError(err);
        setOptimizedSrc(placeholder);
        setIsLoading(false);
        if (onError) onError(err);
      };
      
      // Add loading="lazy" attribute equivalent for modern browsers
      if ('loading' in HTMLImageElement.prototype) {
        (img as any).loading = 'lazy';
      }
      
      img.src = optimizedUrl;
    } else {
      // Immediate loading
      setOptimizedSrc(optimizedUrl);
      setIsLoading(false);
    }
  }, [src, lazyLoad, generateOptimizedUrl, onLoad, onError, placeholder]);
  
  return {
    src: optimizedSrc,
    isLoading,
    error,
    width: width ? (isSlowConnection ? Math.round(width * 0.75) : width) : undefined,
    height: height ? (isSlowConnection ? Math.round(height * 0.75) : height) : undefined
  };
}
